// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — every screen's markup, in one place. Pure string builders: no state, no listeners, so the
// screen machine in main.ts owns all of the wiring and this file can be read as copy.

import { escapeHtml, plural } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { TIERS, tierOf } from './bot';
import { SEAT_NAME } from './palette';
import type { ResultRow } from './duel';
import type { WinReason } from './rules';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(o: { modeId: string; tierId: string; record: [number, number]; muted: boolean }): string {
  const mode = modeOf(o.modeId);
  const tier = tierOf(o.tierId);
  return `
  <section class="screen menu">
    <header class="brand">
      <h1>Torc</h1>
      <p class="tagline">Ride a torc, turn the studs it jumps, and take a line — every line you take costs you a torc.</p>
    </header>

    <div class="picker" role="group" aria-label="Field">
      <h2>Field</h2>
      <div class="chips">${MODE_IDS.map(
        (id) =>
          `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(modeOf(id).name)}</button>`,
      ).join('')}</div>
      <p class="blurb">${escapeHtml(mode.blurb)}</p>
    </div>

    <div class="picker" role="group" aria-label="Opponent">
      <h2>Opponent</h2>
      <div class="chips">${TIERS.map(
        (t) =>
          `<button class="chip${t.id === o.tierId ? ' on' : ''}" data-tier="${t.id}" aria-pressed="${t.id === o.tierId}">${escapeHtml(t.name)}</button>`,
      ).join('')}</div>
      <p class="blurb">${escapeHtml(tier.blurb)}</p>
    </div>

    <div class="menu-actions">
      <button class="btn primary" id="playSolo">Play</button>
      <button class="btn" id="playFriends">Play with a friend</button>
      <button class="btn ghost" id="help">How to play</button>
      <button class="btn ghost" id="about">About</button>
      <button class="btn ghost" id="mute" aria-pressed="${o.muted}">${o.muted ? 'Sound off' : 'Sound on'}</button>
    </div>

    <p class="record">On ${escapeHtml(mode.name)}: <b>${o.record[0]}</b> won · <b>${o.record[1]}</b> lost</p>
  </section>`;
}

export function helpHtml(mode: Mode): string {
  return `
  <h2>How to play</h2>
  <button class="sheet-close iconbtn" aria-label="Close">✕</button>
  <ol class="how">
    <li>You each <b>set ${plural(mode.rings, 'torc')} down</b> on empty points, one at a time.</li>
    <li>On your turn, <b>ride one torc</b> in a straight line. It <b>leaves a stud behind</b> on the point it came from.</li>
    <li>A torc may glide over empty points, or <b>jump one unbroken run of studs</b> and land on the first empty point past it — and <b>every stud it jumped turns over</b>, including your own.</li>
    <li>Get <b>${escapeHtml(mode.rule)}</b> of your studs and you take that line off the field — but <b>you must give up one of your torcs</b> to do it. <b>${mode.target} lines wins.</b></li>
  </ol>
  <p class="fineprint">A line can appear on your opponent's turn too — their jump can turn your studs the right way up. If it does, they hand you the line, and you pay a torc for it just the same.</p>
  <div class="sheet-actions"><button class="btn primary sheet-close">Got it</button></div>`;
}

export function aboutHtml(): string {
  return `
  <h2>About Torc</h2>
  <button class="sheet-close iconbtn" aria-label="Close">✕</button>
  <p>A two-player abstract about a cost you cannot avoid: the only way to score is to give away the piece you were scoring with. Every torc you take off the field is a point and a weakness at the same time.</p>
  <p>Play it alone against a search engine that reads several plies ahead, or send a friend a link and play the same board, move for move, straight between your two browsers.</p>
  <h3>Multiplayer, and what it costs you</h3>
  <p>There is <strong>no game server</strong>. Two browsers connect directly to each other over WebRTC; a public signalling relay is used only to introduce them, and no board, name or result is ever stored anywhere. Connecting exchanges network addresses with the person you invited, which is the ordinary price of a direct connection.</p>
  <h3>Privacy</h3>
  <p>No cookies, no fingerprinting, no third-party fonts, no accounts. Anonymous, cookie-less page-view counts come from Cloudflare Web Analytics, and nothing else is collected. Everything you see is drawn and every sound is synthesised as you play — there are no image or audio files.</p>
  <p class="fineprint">Torc is an original implementation of a public game mechanic. The name, the artwork, the copy and the code are all our own.</p>
  <div class="sheet-actions"><button class="btn primary sheet-close">Close</button></div>`;
}

const REASON: Record<WinReason, string> = {
  none: 'Round over',
  claims: 'Lines taken',
  stalled: 'The field jammed — nobody could ride',
  walkover: 'Your opponent left',
  nocontest: 'The round was stopped',
  draw: 'The field jammed with the score level',
};

export function resultsHtml(o: {
  rows: ResultRow[];
  mode: Mode;
  reason: WinReason;
  winner: number;
  tally: [number, number];
  tallyLabels: [string, string];
  round: number;
  fault: null | 'desync' | 'build';
}): string {
  const head =
    o.fault === 'build'
      ? 'Different builds'
      : o.winner >= 0
        ? `${escapeHtml(o.rows[o.winner].name)} wins`
        : o.reason === 'draw'
          ? 'A draw'
          : 'Round over';

  const note =
    o.fault === 'build'
      ? 'You and your opponent are running different versions of Torc. Reload and the next round will match.'
      : o.fault === 'desync'
        ? 'The two boards stopped agreeing, so the round was stopped. Nobody is credited with a win.'
        : REASON[o.reason];

  // EVERY player's breakdown, always — and the line each of them never closed, which is the thing a
  // summary that only reflects you back at yourself can never show.
  const rows = o.rows
    .map(
      (r) => `
      <div class="rrow${r.won ? ' won' : ''}${r.side === 1 ? ' teal' : ' amber'}">
        <div class="rname">
          <span class="pip ${r.side === 0 ? 'p0' : 'p1'}"></span>
          <b>${escapeHtml(r.name)}</b>
          ${r.isSelf ? '<span class="tag">you</span>' : ''}${r.isBot ? '<span class="tag">engine</span>' : ''}
          ${r.left ? '<span class="tag warn">left</span>' : ''}
        </div>
        <div class="rscore"><b>${r.claims}</b><span>${r.claims === 1 ? 'line' : 'lines'}</span></div>
        <dl class="rstats">
          <div><dt>Rides</dt><dd>${r.rides}</dd></div>
          <div><dt>Jumps</dt><dd>${r.jumps}</dd></div>
          <div><dt>Studs turned</dt><dd>${r.flips}</dd></div>
          <div><dt>Longest line taken</dt><dd>${r.bestClaim || '—'}</dd></div>
          <div><dt>Studs left standing</dt><dd>${r.studs}</dd></div>
          <div><dt>Best line unfinished</dt><dd>${r.unfinished}${r.unfinished >= o.mode.runLen ? '' : ` / ${o.mode.runLen}`}</dd></div>
        </dl>
      </div>`,
    )
    .join('');

  return `
    <h2 class="rhead">${escapeHtml(head)}</h2>
    <p class="rnote">${escapeHtml(note)} · ${escapeHtml(o.mode.name)} · round ${o.round}</p>
    <canvas id="finalBoard" class="finalboard" width="640" height="640" aria-label="The field as it finished"></canvas>
    <div class="rrows">${rows}</div>
    <p class="tally">Match: <b>${o.tally[0]}</b> ${escapeHtml(o.tallyLabels[0])} — <b>${o.tally[1]}</b> ${escapeHtml(o.tallyLabels[1])}</p>`;
}

export const seatLabel = (side: number): string => SEAT_NAME[side] ?? '';
